import { useCallback, useEffect, useMemo, useRef } from 'react';
import { DateTime } from 'luxon';
import {
  createParser,
  createSerializer,
  parseAsIsoDateTime,
  parseAsStringLiteral,
  useQueryStates,
} from 'nuqs';
import { useI18nContext } from '@/i18n/i18n-react';
import { getLuxonLocale } from '@/lib/utils';
import type { Granularity } from '@/components/sales-chart';

export const PREDEFINED_RANGE_VALUES = [
  'today',
  'yesterday',
  'week_to_date',
  'last_7_days',
  'month_to_date',
  'last_30_days',
  'last_3_months',
  'last_6_months',
  'year_to_date',
  'last_12_months',
] as const;

export type PredefinedRange = (typeof PREDEFINED_RANGE_VALUES)[number];

const GRANULARITY_VALUES: Granularity[] = [
  'minute',
  'hour',
  'day',
  'week',
  'month',
];

const DEFAULT_RANGE: PredefinedRange = 'last_30_days';
const DEFAULT_GRANULARITY: Granularity = 'day';

const parseAsGranularity = createParser({
  parse(query) {
    return (GRANULARITY_VALUES as string[]).includes(query)
      ? (query as Granularity)
      : null;
  },
  serialize(value: Granularity) {
    return value;
  },
});

const parseAsPredefinedRange = parseAsStringLiteral(PREDEFINED_RANGE_VALUES);

const filterSearchParams = {
  range: parseAsPredefinedRange,
  from: parseAsIsoDateTime,
  to: parseAsIsoDateTime,
  granularity: parseAsGranularity,
};

export const serializeMetricsFilter = createSerializer(filterSearchParams);

export interface MetricsDateFilter {
  fromDate: string | null;
  setFromDate: (date: string) => void;
  toDate: string | null;
  setToDate: (date: string) => void;
  predefinedDateRange: string | undefined;
  setPredefinedDateRange: (value: string | undefined) => void;
  granularity: Granularity;
  setGranularity: (granularity: Granularity) => void;
  predefinedDateRanges: {
    label: string;
    value: string;
    from: string;
    to: string;
  }[];
  dateFilterLabel: string;
  /** Search params object for building URLs that carry the current filter. */
  searchParams: {
    range: PredefinedRange | null;
    from: Date | null;
    to: Date | null;
    granularity: Granularity | null;
  };
}

export function useMetricsDateFilter(): MetricsDateFilter {
  const { LL, locale } = useI18nContext();

  const [params, setParams] = useQueryStates(filterSearchParams, {
    history: 'replace',
  });

  const predefinedDateRanges = useMemo(
    () => [
      {
        label: LL.salesChart.predefinedRanges.today(),
        value: 'today',
        from: DateTime.now().startOf('day').toISO(),
        to: DateTime.now().toISO(),
      },
      {
        label: LL.salesChart.predefinedRanges.yesterday(),
        value: 'yesterday',
        from: DateTime.now().minus({ days: 1 }).startOf('day').toISO(),
        to: DateTime.now().minus({ days: 1 }).endOf('day').toISO(),
      },
      {
        label: LL.salesChart.predefinedRanges.weekToDate(),
        value: 'week_to_date',
        from: DateTime.now().startOf('week').toISO(),
        to: DateTime.now().toISO(),
      },
      {
        label: LL.salesChart.predefinedRanges.last7Days(),
        value: 'last_7_days',
        from: DateTime.now().minus({ days: 7 }).startOf('day').toISO(),
        to: DateTime.now().toISO(),
      },
      {
        label: LL.salesChart.predefinedRanges.monthToDate(),
        value: 'month_to_date',
        from: DateTime.now().startOf('month').toISO(),
        to: DateTime.now().toISO(),
      },
      {
        label: LL.salesChart.predefinedRanges.last30Days(),
        value: 'last_30_days',
        from: DateTime.now().minus({ days: 30 }).startOf('day').toISO(),
        to: DateTime.now().toISO(),
      },
      {
        label: LL.salesChart.predefinedRanges.last3Months(),
        value: 'last_3_months',
        from: DateTime.now().minus({ months: 3 }).toISO(),
        to: DateTime.now().toISO(),
      },
      {
        label: LL.salesChart.predefinedRanges.last6Months(),
        value: 'last_6_months',
        from: DateTime.now().minus({ months: 6 }).toISO(),
        to: DateTime.now().toISO(),
      },
      {
        label: LL.salesChart.predefinedRanges.yearToDate(),
        value: 'year_to_date',
        from: DateTime.now().startOf('year').toISO(),
        to: DateTime.now().toISO(),
      },
      {
        label: LL.salesChart.predefinedRanges.last12Months(),
        value: 'last_12_months',
        from: DateTime.now().minus({ months: 12 }).toISO(),
        to: DateTime.now().toISO(),
      },
    ],
    [LL]
  );

  // Validate cross-field state and repair the URL. nuqs handles invalid
  // single-param values (invalid ISO / unknown range / unknown granularity) by
  // returning null, which triggers the default. Here we handle invalid
  // *combinations* of otherwise-valid params.
  useEffect(() => {
    const hasFrom = Boolean(params.from);
    const hasTo = Boolean(params.to);
    const hasBothDates = hasFrom && hasTo;
    const hasOnlyOneDate = hasFrom !== hasTo;

    if (hasOnlyOneDate) {
      void setParams({ from: null, to: null });
      return;
    }

    if (hasBothDates && params.from! > params.to!) {
      void setParams({ from: null, to: null });
      return;
    }

    if (hasBothDates && params.range) {
      void setParams({ range: null });
    }
  }, [params.from, params.to, params.range, setParams]);

  // Resolve the effective fromDate / toDate in ISO strings.
  const { fromDate, toDate, predefinedDateRange } = useMemo(() => {
    const customFrom = params.from?.toISOString() ?? null;
    const customTo = params.to?.toISOString() ?? null;

    if (customFrom && customTo) {
      return {
        fromDate: customFrom,
        toDate: customTo,
        predefinedDateRange: undefined as string | undefined,
      };
    }

    const range = params.range ?? DEFAULT_RANGE;
    const match = predefinedDateRanges.find((r) => r.value === range);
    return {
      fromDate: match?.from ?? null,
      toDate: match?.to ?? null,
      predefinedDateRange: match?.value,
    };
  }, [params.from, params.to, params.range, predefinedDateRanges]);

  const granularity: Granularity = params.granularity ?? DEFAULT_GRANULARITY;

  // DateRangeFilter emits setPredefinedDateRange + setFromDate + setToDate in the
  // same tick when the user picks a preset. We want the URL to store only
  // `range` in that case (not the evaluated dates). This ref remembers the
  // just-set preset so the following setFromDate/setToDate calls can be
  // ignored when they carry the preset's own from/to values.
  const pendingPresetRef = useRef<PredefinedRange | null>(null);

  const setPredefinedDateRange = useCallback(
    (value: string | undefined) => {
      if (value === undefined) {
        void setParams({ range: null });
        return;
      }
      const isValid = (PREDEFINED_RANGE_VALUES as readonly string[]).includes(
        value
      );
      const next = isValid ? (value as PredefinedRange) : DEFAULT_RANGE;
      pendingPresetRef.current = next;
      queueMicrotask(() => {
        pendingPresetRef.current = null;
      });
      void setParams({ range: next, from: null, to: null });
    },
    [setParams]
  );

  const setFromDate = useCallback(
    (date: string) => {
      if (pendingPresetRef.current) {
        const match = predefinedDateRanges.find(
          (r) => r.value === pendingPresetRef.current
        );
        if (match?.from === date) return;
      }
      const parsed = DateTime.fromISO(date);
      void setParams({
        from: parsed.isValid ? parsed.toJSDate() : null,
        range: null,
      });
    },
    [setParams, predefinedDateRanges]
  );

  const setToDate = useCallback(
    (date: string) => {
      if (pendingPresetRef.current) {
        const match = predefinedDateRanges.find(
          (r) => r.value === pendingPresetRef.current
        );
        if (match?.to === date) return;
      }
      const parsed = DateTime.fromISO(date);
      void setParams({
        to: parsed.isValid ? parsed.toJSDate() : null,
        range: null,
      });
    },
    [setParams, predefinedDateRanges]
  );

  const setGranularity = useCallback(
    (value: Granularity) => {
      void setParams({ granularity: value });
    },
    [setParams]
  );

  const dateFilterLabel = useMemo(
    () =>
      predefinedDateRanges?.find((g) => g.value === predefinedDateRange)
        ?.label ||
      `${fromDate && DateTime.fromISO(fromDate).setLocale(getLuxonLocale(locale)).toLocaleString()} - ${
        toDate &&
        DateTime.fromISO(toDate)
          .setLocale(getLuxonLocale(locale))
          .toLocaleString()
      }`,
    [predefinedDateRange, fromDate, toDate, locale, predefinedDateRanges]
  );

  return {
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    predefinedDateRange,
    setPredefinedDateRange,
    granularity,
    setGranularity,
    predefinedDateRanges,
    dateFilterLabel,
    searchParams: params,
  };
}
